import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import Loader from 'components/atoms/loader';
import Pagination from 'components/atoms/pagination';
import { ApplicationState } from 'store';
import { AcervoTypes, JuridicoFacilState } from 'store/modules/juridico_facil_store/types';

import * as J from './styles';

interface ResultadosProps {
  termo: string;
  pagina: number;
  onPaginar: (pagina: number) => void;
  onAbrir: (item: AcervoTypes) => void;
}

export const normalizar = (texto: string) =>
  texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const semHtml = (texto: string) =>
  texto
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

export const siglaTribunal = (court?: string | null) => (court ? court.replace('_', '-') : '');

// O texto exibido é o original (com acentos); o casamento com os termos
// buscados é feito sem acento e sem caixa, como a busca do banco
const destacar = (texto: string, termos: string[]) => {
  // Chave = posição do trecho no texto (trechos iguais podem se repetir)
  let posicao = 0;
  return texto.split(/(\s+)/).map((parte) => {
    const chave = posicao;
    posicao += parte.length;
    const bate = termos.some((termo) => normalizar(parte).includes(termo));
    return bate ? (
      <mark key={chave}>{parte}</mark>
    ) : (
      <React.Fragment key={chave}>{parte}</React.Fragment>
    );
  });
};

// No acervo curado o campo já pode vir rotulado ("Acórdão: 354/2016" nas
// súmulas); no JusBrasil é o número do processo
export const referenciaDecisao = (acordao?: string | null, fonte?: string, tipo?: string) => {
  if (!acordao) return '';
  if (fonte === 'jusbrasil') return `Processo ${acordao}`;
  if (/^ac[óo]rd[ãa]o/i.test(acordao.trim())) return acordao;
  return tipo === 'Jurisprudência' ? `Acórdão ${acordao}` : acordao;
};

const Resultados: React.FC<ResultadosProps> = ({ termo, pagina, onPaginar, onAbrir }) => {
  const { juridico, request } = useSelector<ApplicationState, JuridicoFacilState>(
    (state) => state.session.juridico,
  );
  const termos = normalizar(termo)
    .split(/\s+/)
    .filter((parte) => parte.length > 2);
  const itens = juridico?.acervo || [];

  if (request?.search?.loading) return <Loader />;

  return (
    <J.Lista>
      {request?.search?.loaded && itens.length === 0 && (
        <J.Vazio>
          Nenhum resultado para “{termo}”. Tente outra palavra-chave ou amplie os tribunais.
        </J.Vazio>
      )}

      {itens.map((item) => (
        <J.Card
          key={`${item.fonte || 'base'}-${item.id}`}
          type="button"
          onClick={() => onAbrir(item)}
        >
          <header>
            <strong>{item.tipo}</strong>
            {!!item.court && <em>{siglaTribunal(item.court)}</em>}
            <span>{[item.area, item.tema, item.subtema].filter(Boolean).join(' › ')}</span>
          </header>
          <p>{destacar(semHtml(item.texto || item.enunciado || ''), termos)}</p>
          {(item.acordao || item.data_sessao || item.autor_da_tese) && (
            <footer>
              {!!item.acordao && (
                <span>{referenciaDecisao(item.acordao, item.fonte, item.tipo)}</span>
              )}
              {!!item.data_sessao && <span>{moment(item.data_sessao).format('DD/MM/YYYY')}</span>}
              {!!item.autor_da_tese && <span>{item.autor_da_tese}</span>}
            </footer>
          )}
        </J.Card>
      ))}

      {itens.length > 0 && (
        <J.Paginacao>
          <Pagination
            postsPerPage={10}
            totalPosts={juridico?.search_total_page}
            paginate={onPaginar}
            currentPage={pagina}
            firstAndLastPage
          />
        </J.Paginacao>
      )}
    </J.Lista>
  );
};

export default Resultados;
